import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { callFunction } from "../api/client";
import { asText } from "../ui/Facts";

/* One edit for a whole record.
 *
 * The page keeps a draft of the record's own fields (by column, as the text
 * an input shows) and, for a person, the job and the phones and emails. Save
 * sends only what differs from the record to api.edit_record (engine
 * migration 188), which applies it in one transaction under one signal with
 * the editor's name and says what changed. "Use it" on a claim another
 * source made is the same call with one field. docs/ux-decisions.md,
 * Records: viewing and editing. */

export type EditChanges = {
  fields?: Record<string, string>;
  job?: Record<string, string>;
  reach?: { add: { kind: string; value: string }[]; remove: string[] };
};

export type EditResult = {
  signal_id: string;
  fields: string[];
  job: string | null;
  reach_added: number;
  reach_removed: number;
};

export function useRecordEdit({ token, entityType, entityId, values, extra, onSaved }: {
  token: string;
  entityType: "company" | "person" | "property";
  entityId: string;
  /** The record as the api view returned it. */
  values: Record<string, unknown>;
  /** The parts beyond the record's own fields, and how many things they
   *  would change. A person's job and reach. */
  extra?: () => { job?: Record<string, string>; reach?: EditChanges["reach"]; count: number };
  onSaved: (result: EditResult) => void;
}) {
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const changedFields = (): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const [f, v] of Object.entries(draft)) if (v !== asText(values[f])) out[f] = v;
    return out;
  };

  const mutation = useMutation({
    mutationFn: (changes: EditChanges) =>
      callFunction<EditResult>(token, "edit_record", {
        p_entity_type: entityType,
        p_entity_id: entityId,
        p_changes: changes,
        p_note: note.trim() || null,
      }),
    onSuccess: (result) => {
      setError(null);
      setDraft({});
      setNote("");
      queryClient.invalidateQueries({ queryKey: [entityType, entityId] });
      queryClient.invalidateQueries({ queryKey: ["provenance", entityType, entityId] });
      queryClient.invalidateQueries({ queryKey: ["contested-facts", entityType, entityId] });
      onSaved(result);
    },
    // The lake's own sentence: api.edit_record says what it refused and why.
    onError: (e: Error) => setError(e.message),
  });

  const ex = extra?.();
  const fields = changedFields();
  const pending = Object.keys(fields).length + (ex?.count ?? 0);

  return {
    draft,
    setField: (field: string, value: string) => setDraft((d) => ({ ...d, [field]: value })),
    note,
    setNote,
    pending,
    save: () => {
      const changes: EditChanges = { fields: changedFields() };
      const now = extra?.();
      if (now?.job && Object.keys(now.job).length) changes.job = now.job;
      if (now?.reach && (now.reach.add.length || now.reach.remove.length)) changes.reach = now.reach;
      mutation.mutate(changes);
    },
    /** Take a value another source asserted, as an edit of that one field. */
    applyClaim: (field: string, value: string) => mutation.mutate({ fields: { [field]: value } }),
    saving: mutation.isPending,
    error,
    reset: () => { setDraft({}); setNote(""); setError(null); },
  };
}
